import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import IconStars from '../../../assets/icon-stars.svg';
import { LIST_AGENT_CREDITS } from './constants';

const HOW_IT_WORKS_STEPS = [
  { number: 1, text: 'You define who you want to reach' },
  { number: 2, text: 'The agent finds relevant people based on your criteria' },
  { number: 3, text: 'The agent sends invitations to join this list' },
];

export const EmptyState = ({ availableCredits, onSetupAgent, isLoading }) => {
  const hasEnoughCredits = availableCredits >= LIST_AGENT_CREDITS;

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        height: '100%',
        px: '16px',
        py: '48px',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '40px',
          maxWidth: '480px',
          width: '100%',
          margin: '0 auto',
        }}
      >
        {/* Header Section */}
        <Flex sx={{ gap: '8px', alignItems: 'flex-start' }}>
          <Flex sx={{ flexDirection: 'column', gap: '8px', flex: 1 }}>
            <Text
              sx={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'text.dark',
                lineHeight: '1.4',
              }}
            >
              Automatically grow this list
            </Text>
            <Text
              sx={{
                fontSize: '16px',
                color: 'text.main',
                lineHeight: '1.4',
              }}
            >
              Launch an agent that finds relevant people outside your network and invites them to join this list, so
              you&apos;re ready when the need comes.
            </Text>
          </Flex>
          <Image src={IconStars} alt="AI Agent" width="32px" height="32px" sx={{ flexShrink: 0 }} />
        </Flex>

        {/* How it works Section */}
        <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
          <Text
            sx={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'purple.500',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              background: 'linear-gradient(90deg, #4900FF 0%, #D400FF 50%, #FF9257 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How it works
          </Text>
          <Flex sx={{ flexDirection: 'column', gap: '16px', position: 'relative' }}>
            {/* Vertical line connecting steps */}
            <Box
              sx={{
                position: 'absolute',
                left: '11px',
                top: '24px',
                bottom: '24px',
                width: '2px',
                background: 'linear-gradient(180deg, #8A75F0 0%, #8A75F0 100%)',
                borderRadius: '1px',
              }}
            />
            {HOW_IT_WORKS_STEPS.map((step) => (
              <Flex key={step.number} sx={{ gap: '8px', alignItems: 'center' }}>
                <Flex
                  sx={{
                    minWidth: '24px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    bg: 'purple.300',
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {step.number}
                </Flex>
                <Text
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'text.dark',
                    lineHeight: '1.4',
                  }}
                >
                  {step.text}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>

        {/* Credits and Button Section */}
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
            <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Text sx={{ fontSize: '16px', fontWeight: '500', color: 'text.dark' }}>Total</Text>
              <Text sx={{ fontSize: '16px', fontWeight: '500', color: 'text.dark' }}>{LIST_AGENT_CREDITS} credits</Text>
            </Flex>
            <Flex sx={{ justifyContent: 'flex-end' }}>
              <Text sx={{ fontSize: '14px', color: 'text.main' }}>{availableCredits} available credits</Text>
            </Flex>
          </Flex>

          <Button
            variant="primary"
            width="100%"
            justifyContent="center"
            onClick={onSetupAgent}
            disabled={!hasEnoughCredits || isLoading}
            loading={isLoading}
          >
            Set up agent
          </Button>

          {!hasEnoughCredits && (
            <Text sx={{ fontSize: '12px', color: 'orange.600', textAlign: 'center' }}>
              Not enough credits available. Contact us to top up your balance.
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

EmptyState.propTypes = {
  availableCredits: PropTypes.number.isRequired,
  onSetupAgent: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

EmptyState.defaultProps = {
  isLoading: false,
};
